import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Minus,
  MoreVertical,
  Target,
} from '../../../bravosIcons';
import { formatLibraryDate } from '../../../../../lib/dateUtils';
import LibraryEmptyState from '../components/LibraryEmptyState';
import {
  LIB_WORKOUT_PLANS_PAGE_SIZE,
  type LibraryWorkoutPlansApi,
} from '../../hooks/useLibraryWorkoutPlans';
import { isTrainerOwnedLibraryRow } from '../../utils/workoutLibraryRowUtils';

export type WorkoutPlansTableDesktopProps = {
  pl: LibraryWorkoutPlansApi;
  workoutPlans: any[];
  profileId: string | undefined;
  selectedWorkoutPlans: Set<string>;
  openWorkoutPlanMenu: string | null;
  setOpenWorkoutPlanMenu: (id: string | null) => void;
  workoutPlanMenuRefs: React.MutableRefObject<Record<string, HTMLDivElement | null>>;
  trainerPlanDuplicateInFlight: boolean;
  libPlansPageSelectionState: 'none' | 'some' | 'all';
  onToggleSelectPlansPage: () => void;
  onSelectWorkoutPlan: (planId: string) => void;
  onEditWorkoutPlan: (planId: string) => void;
  onDuplicateWorkoutPlan: (planId: string) => void;
  onAssignWorkoutPlan: (planId: string) => void;
  onDeleteWorkoutPlan: (planId: string, planName: string) => void;
  onCreatePlan: () => void;
};

/** Desktop Library — Workout Plans tab table, empty states, pagination (§4 / §5). */
export function WorkoutPlansTableDesktop({
  pl,
  workoutPlans,
  profileId,
  selectedWorkoutPlans,
  openWorkoutPlanMenu,
  setOpenWorkoutPlanMenu,
  workoutPlanMenuRefs,
  trainerPlanDuplicateInFlight,
  libPlansPageSelectionState,
  onToggleSelectPlansPage,
  onSelectWorkoutPlan,
  onEditWorkoutPlan,
  onDuplicateWorkoutPlan,
  onAssignWorkoutPlan,
  onDeleteWorkoutPlan,
  onCreatePlan,
}: WorkoutPlansTableDesktopProps) {
  const navigate = useNavigate();

  if (pl.libPlansEmptyVariant !== null) {
    return (
      <LibraryEmptyState
        variant={pl.libPlansEmptyVariant}
        tab="workoutPlans"
        searchQuery={pl.workoutPlanNameFilter}
        hasActiveFilters={pl.libPlansHasActiveControls}
        onClearAll={pl.handleClearAllPlans}
        onCreate={onCreatePlan}
      />
    );
  }

  return (
    <div className="lib-table-wrap">
      <table className="lib-table">
        <thead>
          <tr>
            <th className="lib-col-select" aria-label="Select column">
              <button
                type="button"
                className={`lib-checkbox ${libPlansPageSelectionState === 'all' ? 'is-checked' : libPlansPageSelectionState === 'some' ? 'is-indeterminate' : ''}`}
                onClick={onToggleSelectPlansPage}
                aria-label="Select all workout plans on this page"
                aria-checked={libPlansPageSelectionState === 'all' ? true : libPlansPageSelectionState === 'some' ? 'mixed' : false}
                role="checkbox"
              >
                {libPlansPageSelectionState === 'all' && <Check />}
                {libPlansPageSelectionState === 'some' && <Minus />}
              </button>
            </th>
            <th className="lib-col-index">№</th>
            <th
              className={`is-sortable ${(pl.libPlanSort === 'name-asc' || pl.libPlanSort === 'name-desc') ? 'is-sorted' : ''}`}
              onClick={() => pl.handleLibPlanHeaderSort('name')}
              aria-sort={pl.libPlanSort === 'name-asc' ? 'ascending' : pl.libPlanSort === 'name-desc' ? 'descending' : 'none'}
            >
              <span className="lib-th-inner">
                Name
                {pl.libPlanSort === 'name-asc'
                  ? <ChevronUp />
                  : pl.libPlanSort === 'name-desc'
                    ? <ChevronDown />
                    : <ChevronDown style={{ opacity: 0.4 }} />}
              </span>
            </th>
            <th
              className={`is-sortable ${(pl.libPlanSort === 'duration-asc' || pl.libPlanSort === 'duration-desc') ? 'is-sorted' : ''}`}
              onClick={pl.handleLibPlanDurationHeaderSort}
              aria-sort={pl.libPlanSort === 'duration-asc' ? 'ascending' : pl.libPlanSort === 'duration-desc' ? 'descending' : 'none'}
            >
              <span className="lib-th-inner">
                Duration
                {pl.libPlanSort === 'duration-asc'
                  ? <ChevronUp />
                  : pl.libPlanSort === 'duration-desc'
                    ? <ChevronDown />
                    : <ChevronDown style={{ opacity: 0.4 }} />}
              </span>
            </th>
            <th
              className={`is-sortable ${(pl.libPlanSort === 'newest' || pl.libPlanSort === 'oldest') ? 'is-sorted' : ''}`}
              onClick={() => pl.handleLibPlanHeaderSort('createdAt')}
              aria-sort={pl.libPlanSort === 'oldest' ? 'ascending' : pl.libPlanSort === 'newest' ? 'descending' : 'none'}
            >
              <span className="lib-th-inner">
                Created
                {pl.libPlanSort === 'oldest'
                  ? <ChevronUp />
                  : pl.libPlanSort === 'newest'
                    ? <ChevronDown />
                    : <ChevronDown style={{ opacity: 0.4 }} />}
              </span>
            </th>
            <th className="lib-col-actions" aria-label="Row actions" />
          </tr>
        </thead>
        <tbody>
          {pl.filteredAndSortedWorkoutPlans.length === 0 ? (
            <tr>
              <td colSpan={6} className="lib-empty">
                <Target aria-hidden="true" />
                <h3>
                  {workoutPlans.length === 0
                    ? 'No workout plans yet'
                    : 'No plans match your filters'}
                </h3>
                <p>
                  {workoutPlans.length === 0
                    ? 'Get started by creating your first workout plan'
                    : 'Try adjusting your filters to see more results'}
                </p>
                {workoutPlans.length === 0 && (
                  <button type="button" className="lib-empty-cta" onClick={onCreatePlan}>
                    Create plan
                  </button>
                )}
              </td>
            </tr>
          ) : (
            pl.libPlansPageRows.map((plan, indexOnPage) => {
              const globalIndex = (pl.libPlansPage - 1) * LIB_WORKOUT_PLANS_PAGE_SIZE + indexOnPage;
              const isSelected = selectedWorkoutPlans.has(plan.id);
              const isMenuOpen = openWorkoutPlanMenu === plan.id;
              const weeks = pl.planWeeksOf(plan);
              const workoutCount = typeof plan.workoutCount === 'number' ? plan.workoutCount : 0;
              const planIsYou = isTrainerOwnedLibraryRow(plan.createdBy, profileId);
              return (
                <tr
                  key={`plan-${plan.id}`}
                  className={`${isSelected ? 'is-selected' : ''}${isMenuOpen ? ' lib-row-menu-open' : ''}`}
                  onClick={(e) => {
                    const target = e.target as HTMLElement;
                    if (
                      target.closest('.lib-checkbox')
                      || target.closest('.lib-kebab')
                      || target.closest('.lib-kebab-menu')
                    ) {
                      return;
                    }
                    navigate(`/trainer-dashboard/workout-plan/${plan.id}`);
                  }}
                >
                  <td className="lib-col-select" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      className={`lib-checkbox ${isSelected ? 'is-checked' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelectWorkoutPlan(plan.id);
                      }}
                      aria-label={`Select row ${plan.name}`}
                      aria-checked={isSelected}
                      role="checkbox"
                    >
                      {isSelected && <Check />}
                    </button>
                  </td>
                  <td className="lib-col-index">
                    <span className="lib-row-index">{String(globalIndex + 1).padStart(2, '0')}</span>
                  </td>
                  <td>
                    <div className="lib-row-name">{plan.name}</div>
                    <div className="lib-row-subtitle">
                      <span className="lib-tnum">{workoutCount}</span>{' '}
                      {workoutCount === 1 ? 'workout' : 'workouts'}
                    </div>
                  </td>
                  <td>
                    {weeks === null ? (
                      <span className="lib-row-date">—</span>
                    ) : (
                      <span className="lib-row-date">
                        {weeks} {weeks === 1 ? 'week' : 'weeks'}
                      </span>
                    )}
                  </td>
                  <td>
                    <span className="lib-row-date">{formatLibraryDate(plan.createdAt)}</span>
                  </td>
                  <td className="lib-col-actions" onClick={(e) => e.stopPropagation()}>
                    <div
                      className="relative inline-block"
                      ref={(el) => { workoutPlanMenuRefs.current[plan.id] = el; }}
                    >
                      <button
                        type="button"
                        className={`lib-kebab${isMenuOpen ? ' is-open' : ''}`}
                        aria-haspopup="menu"
                        aria-expanded={isMenuOpen}
                        aria-label="Workout plan actions"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenWorkoutPlanMenu(isMenuOpen ? null : plan.id);
                        }}
                      >
                        <MoreVertical className="w-4 h-4" />
                      </button>
                      {isMenuOpen && (
                        <>
                          <div
                            className="fixed inset-0 z-[90]"
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenWorkoutPlanMenu(null);
                            }}
                            onTouchStart={(e) => e.stopPropagation()}
                            style={{ touchAction: 'none' }}
                          />
                          <div
                            className="lib-kebab-menu"
                            onClick={(e) => e.stopPropagation()}
                            onTouchStart={(e) => e.stopPropagation()}
                            role="menu"
                          >
                            <button
                              type="button"
                              className="lib-menu-item"
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenWorkoutPlanMenu(null);
                                onEditWorkoutPlan(plan.id);
                              }}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              disabled={trainerPlanDuplicateInFlight}
                              className="lib-menu-item"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (trainerPlanDuplicateInFlight) return;
                                setOpenWorkoutPlanMenu(null);
                                void onDuplicateWorkoutPlan(plan.id);
                              }}
                            >
                              Duplicate
                            </button>
                            <button
                              type="button"
                              className="lib-menu-item"
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenWorkoutPlanMenu(null);
                                onAssignWorkoutPlan(plan.id);
                              }}
                            >
                              Assign to a member
                            </button>
                            {planIsYou && (
                              <>
                                <div className="lib-kebab-menu-divider" role="separator" />
                                <button
                                  type="button"
                                  className="lib-menu-item lib-menu-item--danger"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenWorkoutPlanMenu(null);
                                    onDeleteWorkoutPlan(plan.id, plan.name);
                                  }}
                                >
                                  Delete
                                </button>
                              </>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>

      {pl.filteredAndSortedWorkoutPlans.length > LIB_WORKOUT_PLANS_PAGE_SIZE && (
        <div className="lib-table-foot">
          <div className="lib-foot-meta">
            Showing{' '}
            <strong className="lib-tnum">
              {pl.libPlansRangeStart}–{pl.libPlansRangeEnd}
            </strong>{' '}
            of <strong className="lib-tnum">{pl.filteredAndSortedWorkoutPlans.length}</strong>
          </div>
          <div className="lib-pager" role="navigation" aria-label="Pagination">
            <button
              type="button"
              className="lib-page-btn"
              onClick={() => pl.setLibPlansPage((p) => Math.max(1, p - 1))}
              disabled={pl.libPlansPage === 1}
              aria-label="Previous page"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            {pl.libBuildPageWindow(pl.libPlansPage, pl.libPlansTotalPages).map((p, i) =>
              p === 'ellipsis' ? (
                <span key={`plans-ellipsis-${i}`} className="lib-page-ellipsis" aria-hidden="true">…</span>
              ) : (
                <button
                  key={`plans-page-${p}`}
                  type="button"
                  className={`lib-page-btn ${p === pl.libPlansPage ? 'is-active' : ''}`}
                  onClick={() => pl.setLibPlansPage(p)}
                  aria-label={`Go to page ${p}`}
                  aria-current={p === pl.libPlansPage ? 'page' : undefined}
                >
                  {p}
                </button>
              )
            )}
            <button
              type="button"
              className="lib-page-btn"
              onClick={() => pl.setLibPlansPage((p) => Math.min(pl.libPlansTotalPages, p + 1))}
              disabled={pl.libPlansPage === pl.libPlansTotalPages}
              aria-label="Next page"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
